import pRetry, { AbortError } from 'p-retry';
import { paginateWithConcurrency } from 'query/autoPagination';
import { LIBKEY_ARTICLES, tokenizeRoute } from 'query/routes';

/**
 * One LibKey lookup state. JSON projection of the backend `ArticleResult`
 * value object — see app/concepts/libkey/api/docs/article_result.md for the
 * full breakdown of each `access_type`.
 */
export type LibkeyAccessType =
  | 'full_text_file'
  | 'content_location'
  | 'document_delivery'
  | 'link_resolver'
  | 'article_retracted'
  | 'expression_of_concern'
  | 'problematic_journal'
  | 'unknown'
  | 'error';

export type LibkeyAlertType =
  | 'article_retracted'
  | 'expression_of_concern'
  | 'problematic_journal';

export type LibkeyArticleResult = {
  access_type: LibkeyAccessType;
  url?: string;
  alert?: LibkeyAlertType;
  second_order_retraction_url?: string;
  error_message?: string;
  open_access?: boolean;
};

/**
 * A reference id as held by a study list. The screen / select lists use the
 * numeric ReviewReference id; the extraction list (GraphQL) uses the encoded
 * global id. The endpoint accepts either and echoes back whichever form it
 * received, so the caller can correlate results regardless.
 */
export type LibkeyReferenceId = string | number;

/**
 * The endpoint returns one entry per reference it resolved. `result` is null
 * when the reference has no DOI to look up. `reference_id` is echoed back in
 * the same form it was requested in (see {@link LibkeyReferenceId}).
 */
export type LibkeyArticle = {
  reference_id: LibkeyReferenceId;
  result: LibkeyArticleResult | null;
};

type LibkeyArticlesResponse = {
  articles: LibkeyArticle[];
};

/**
 * Batch size per request. The endpoint accepts more, but we keep batches small
 * so individual requests stay fast and a single slow/failed batch only affects
 * its own references. Tune once LibKey API latency is understood (WEKA-457).
 */
export const LIBKEY_BATCH_SIZE = 25;

/**
 * Limit concurrent in-flight batches to be polite to the upstream LibKey API.
 */
export const LIBKEY_CONCURRENCY_LIMIT = 3;

/**
 * Extra attempts for a batch that fails with a *transient* error (a network
 * error, or a retryable 408/429/>500 status). Lets a brief blip self-heal
 * within the session instead of leaving those references blank until a full
 * page reload. A non-transient response is never retried.
 */
export const LIBKEY_BATCH_RETRY_LIMIT = 2;

/**
 * Backoff before the first retry; p-retry doubles it on each subsequent attempt.
 */
export const LIBKEY_BATCH_RETRY_MIN_TIMEOUT_MS = 500;

// Transient: 408 (request timeout), 429 (rate limited), and gateway/availability
// errors above 500 (502/503/504). A plain 500 is an application error that
// won't change on a retry, and other 4xx are caller errors — neither is retried.
const isRetryableStatus = (status: number): boolean =>
  status === 408 || status === 429 || status > 500;

/**
 * Fetch a single batch, throwing on a non-ok response. A non-transient status
 * (a plain 500 or a 4xx) is wrapped in an {@link AbortError} so p-retry stops
 * immediately; a transient status throws a plain error so p-retry retries it (a
 * network-level failure rejects on its own and is retried too).
 */
const fetchArticleBatch = async (
  requestUrl: string
): Promise<LibkeyArticle[]> => {
  const res = await fetch(requestUrl, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  });

  if (res.ok) {
    const data: LibkeyArticlesResponse = await res.json();
    return data.articles ?? [];
  }

  const message = `LibKey articles batch failed: ${res.status}`;
  throw isRetryableStatus(res.status)
    ? new Error(message)
    : new AbortError(message);
};

/**
 * Fetch LibKey article states for a set of reference ids, batching into groups
 * of {@link LIBKEY_BATCH_SIZE} and requesting each batch concurrently (up to
 * {@link LIBKEY_CONCURRENCY_LIMIT} at a time).
 *
 * The endpoint is a GET that takes the ids as repeated `reference_ids[]` query
 * params (see spec/requests/api/libkey/v1/articles_spec.rb).
 *
 * Batches fail independently: a batch whose request keeps failing after
 * {@link LIBKEY_BATCH_RETRY_LIMIT} retries resolves to no articles rather than
 * rejecting the whole call, so successful batches still surface their links.
 */
export const fetchLibkeyArticles = async (
  reviewId: string,
  referenceIds: LibkeyReferenceId[]
): Promise<LibkeyArticle[]> => {
  if (referenceIds.length === 0) {
    return [];
  }

  if (!reviewId) {
    throw new Error('reviewId is required');
  }

  const responses = await paginateWithConcurrency<
    LibkeyReferenceId,
    LibkeyArticle[]
  >({
    items: referenceIds,
    options: {
      chunkSize: LIBKEY_BATCH_SIZE,
      concurrencyLimit: LIBKEY_CONCURRENCY_LIMIT,
    },
    mapper: async (chunk) => {
      const url = tokenizeRoute(LIBKEY_ARTICLES, { review_id: reviewId });
      const params = new URLSearchParams();
      chunk.forEach((id) => params.append('reference_ids[]', id.toString()));
      const requestUrl = `${url}?${params.toString()}`;

      try {
        return await pRetry(() => fetchArticleBatch(requestUrl), {
          retries: LIBKEY_BATCH_RETRY_LIMIT,
          minTimeout: LIBKEY_BATCH_RETRY_MIN_TIMEOUT_MS,
        });
      } catch {
        // Retries exhausted, or a non-transient failure: this batch contributes
        // no articles. Its references simply render nothing and are re-fetched
        // when the visible set next changes (e.g. paginating) or on a reload.
        return [];
      }
    },
  });

  return responses.flat();
};
